// Records a Listen session for the operator: the video of what he shared plus
// both sides' sound mixed into one track, sent to the Worker in pieces while it
// runs. Adam, 2026-10-05: "for outside calls like this, I just need audio ...
// and transcription", then "if we can share screen for video too". The browser
// does the capture locally; nothing in the other app can see it. The Worker
// joins the pieces onto the call when the session ends (worker/src/recording.ts).

import type { ListenSaved } from "@breeze/core";
import { getUserKey } from "./storage";

const PIECE_MS = 20_000;
const FLUSH_WAIT_MS = 8_000;

function pickType(): string {
  const types = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
  return types.find((t) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) ?? "";
}

export class ListenRecorder {
  private readonly code: string;
  private recorder: MediaRecorder | null = null;
  private video: MediaStreamTrack | null = null;
  private context: AudioContext | null = null;
  private mixer: MediaStreamAudioDestinationNode | null = null;
  private readonly sources = new Map<string, MediaStreamAudioSourceNode>();
  private n = 0;
  private uploads: Promise<void> = Promise.resolve();
  private stopped: Promise<ListenSaved> | null = null;
  private lost = 0;
  /** Pieces lost over the whole session; `lost` counts only the latest run. */
  private lostAny = 0;
  private sent = 0;

  constructor(code: string) {
    this.code = code;
  }

  /** Mixes another track of sound into the recording. Safe to call again for
   * a re-shared side: the old source for that name is let go. */
  addAudio(name: string, track: MediaStreamTrack): void {
    if (this.stopped || typeof AudioContext === "undefined") return;
    try {
      this.context ??= new AudioContext();
      this.mixer ??= this.context.createMediaStreamDestination();
      this.sources.get(name)?.disconnect();
      const source = this.context.createMediaStreamSource(new MediaStream([track]));
      source.connect(this.mixer);
      this.sources.set(name, source);
      void this.context.resume().catch(() => undefined);
    } catch {
      // Recording is a bonus; the transcript path does not depend on it.
    }
  }

  /** Holds the recording still, or carries on. The pieces stay one file: the
   * stretch paused is simply not in it. */
  setPaused(paused: boolean): void {
    const recorder = this.recorder;
    if (!recorder || this.stopped) return;
    try {
      if (paused && recorder.state === "recording") recorder.pause();
      else if (!paused && recorder.state === "paused") recorder.resume();
    } catch {
      // Left as it was.
    }
  }

  /** What the recording is doing, for the screen: "trouble" when the last
   * pieces did not reach the Worker. */
  state(): "waiting" | "on" | "paused" | "trouble" {
    if (!this.recorder || this.stopped) return "waiting";
    if (this.lost >= 2) return "trouble";
    return this.recorder.state === "paused" ? "paused" : "on";
  }

  /** Pieces the Worker has confirmed. */
  piecesSent(): number {
    return this.sent;
  }

  /** Whether the recording is running. */
  hasStarted(): boolean {
    return !!this.recorder;
  }

  /** Starts once there is video to record. Later calls do nothing: a second
   * recorder would start a second file, and the pieces are joined as one. */
  startWith(video: MediaStreamTrack | null): void {
    if (this.recorder || this.stopped || !this.mixer || typeof MediaRecorder === "undefined") {
      // A later share's video is not recorded: let it go.
      if (video && video !== this.video) video.stop();
      return;
    }
    this.video = video;
    try {
      const tracks = [...(video ? [video] : []), ...this.mixer.stream.getAudioTracks()];
      const type = pickType();
      const recorder = new MediaRecorder(new MediaStream(tracks), {
        ...(type ? { mimeType: type } : {}),
        videoBitsPerSecond: 300_000,
        audioBitsPerSecond: 64_000,
      });
      recorder.ondataavailable = (e: BlobEvent): void => {
        if (e.data.size) this.send(e.data);
      };
      recorder.start(PIECE_MS);
      this.recorder = recorder;
    } catch {
      this.recorder = null;
    }
  }

  private send(blob: Blob): void {
    const n = this.n++;
    this.uploads = this.uploads.then(async () => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const key = getUserKey();
          const res = await fetch(`/api/listen/media/${encodeURIComponent(this.code)}/${n}`, {
            method: "PUT",
            headers: { ...(key ? { authorization: `Bearer ${key}` } : {}), "content-type": blob.type || "video/webm" },
            body: blob,
          });
          if (!res.ok) throw new Error(String(res.status));
          this.sent += 1;
          this.lost = 0;
          return;
        } catch {
          // One more try, then the piece is lost and the rest still join.
        }
      }
      this.lost += 1;
      this.lostAny += 1;
    });
  }

  /** Stops, and waits (a few seconds at most) for the last piece to arrive.
   * Says how the recording ended up: "none" when it never started, "partial"
   * when a piece was lost or the wait ran out first. */
  flush(): Promise<ListenSaved> {
    if (this.stopped) return this.stopped;
    const recorder = this.recorder;
    this.stopped = new Promise<ListenSaved>((resolve) => {
      const finish = (): void => {
        for (const source of this.sources.values()) source.disconnect();
        this.sources.clear();
        this.video?.stop();
        this.video = null;
        void this.context?.close().catch(() => undefined);
        if (!recorder) {
          resolve("none");
          return;
        }
        let everyPiece = false;
        const uploaded = this.uploads.then(() => {
          everyPiece = true;
        });
        void Promise.race([uploaded, new Promise<void>((r) => setTimeout(r, FLUSH_WAIT_MS))]).then(() => {
          if (this.sent === 0 && this.lostAny === 0) resolve("none");
          else resolve(everyPiece && this.lostAny === 0 ? "saved" : "partial");
        });
      };
      if (!recorder || recorder.state === "inactive") {
        finish();
        return;
      }
      recorder.addEventListener("stop", finish, { once: true });
      try {
        recorder.stop();
      } catch {
        finish();
      }
    });
    return this.stopped;
  }
}
